import {
  ActionIcon,
  Button,
  CardProps,
  Divider,
  Drawer,
  DrawerProps,
  Flex,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useDisclosure} from "@mantine/hooks";
import {useNoticeMutation} from "../../../hooks/useNoticeMutation";
import {useCallback, useMemo, useState} from "react";
import {
  adjustToTimeZoneSafely,
  DatabaseCode,
  extractErrorMessage,
  getDatabaseCodeSafely,
  getObjectId,
  isDatabaseCode,
  NoticeCreateRequestDTO,
  NoticeEntity,
  noticeSeverity,
  useLazySearchUserNoticesQuery,
  usePagedRtkQuery
} from "@ots-frontend/ots-data";
import {NoticeFormModal, NoticeFormValues} from "../../form/NoticeForm/NoticeForm";
import {Card} from "../../card/Card/Card";
import {NoticeSeverityBadge} from "../../badge/NoticeSeverityBadge/NoticeSeverityBadge";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {IconBuilding, IconEye, IconEyeOff, IconPlus, IconRefresh, IconUsers, IconWorld} from "@tabler/icons-react";
import {NoticeModal} from "../../modal/NoticeModal/NoticeModal";
import {Badge} from "../../badge/Badge/Badge";

export type NoticeDrawerProps = Omit<DrawerProps, 'title'> & {}

export const NoticeDrawer = (props: NoticeDrawerProps) => {
  const {...rest} = props

  const {t} = useTranslation(["common"]);

  const [selectedNotice, setSelectedNotice] = useState<NoticeEntity | null>(null);

  const noticeFormModalDisclosure = useDisclosure(false);
  const noticeFormModalOpened = noticeFormModalDisclosure[0]
  const noticeFormModalHandlers = noticeFormModalDisclosure[1]

  const noticeModalDisclosure = useDisclosure(false);
  const noticeModalOpened = noticeModalDisclosure[0]
  const noticeModalHandlers = noticeModalDisclosure[1]

  const [triggerSearch] = useLazySearchUserNoticesQuery();

  const resetKey = useMemo(() => JSON.stringify({}), []);

  const {
    items,
    total,
    hasMore,
    isFetching,
    error,
    refresh,
    loadMore,
  } = usePagedRtkQuery({
    args: {
      active: true,
    },
    pageSize: 25,
    resetKey: resetKey,
    trigger: triggerSearch,
    mapItem: NoticeEntity.from,
    getId: (n) => n.id,
  });

  const {
    createNotice,
    isCreating,
  } = useNoticeMutation()

  const onNoticeSubmit = useCallback((values: NoticeFormValues) => {
    const dto: NoticeCreateRequestDTO = {
      kind: values.kind.code,
      category: values.category.code,
      severity: values.severity.code,
      title: values.title,
      bodyText: values.bodyText,
      expiresDttm: adjustToTimeZoneSafely(values.expiresDate)?.getTime(),
      regionIds: values.regions?.map(getObjectId),
      companyIds: values.companies?.map(getObjectId),
      audienceCapabilities: values.audienceCapabilities?.map(getDatabaseCodeSafely),
    }

    createNotice(dto, {onSuccess: noticeFormModalHandlers.close})

  }, [createNotice, noticeFormModalHandlers])

  const onNoticeClick = useCallback((notice: NoticeEntity) => {
    setSelectedNotice(notice)
    noticeModalHandlers.open()
  }, [noticeModalHandlers])

  const onNoticeModelClose = useCallback(() => {
    setSelectedNotice(null)
    noticeModalHandlers.close()
  }, [noticeModalHandlers])

  return (
    <Drawer
      {...rest}
      title={t("common.entity.notice.plural", "Notices")}
    >
      <Stack style={{height: "calc(100vh - 120px)"}} spacing="sm">
        <Group position="apart" spacing="xs">
          <Text size="sm" c="dimmed">
            {total} {t("common.entity.notice.plural", "Notices")}
          </Text>
          <Group position="right" spacing="xs" mt="md">
            <ActionIcon variant="subtle" onClick={refresh}>
              <IconRefresh size={16}/>
            </ActionIcon>
            <ActionIcon color="green" onClick={noticeFormModalHandlers.open}>
              <IconPlus size={16}/>
            </ActionIcon>
          </Group>
        </Group>

        <Divider/>

        {(isFetching && items.length === 0) && (
          <Group spacing="center" py="md">
            <Loader size="sm"/>
          </Group>
        )}

        {error && (
          <Text c="red" size="sm">
            {extractErrorMessage(error)}
          </Text>
        )}

        {!isFetching && !error && items.length === 0 && (
          <Text size="sm" c="dimmed">
            No active notices.
          </Text>
        )}

        {items.length > 0 && (
          <ScrollArea offsetScrollbars style={{flex: 1, minHeight: 0}}>
            <Stack spacing="xs">
              {items.map((n) => (
                <NoticeItem
                  key={n.id}
                  notice={n}
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder={true}
                  onClick={onNoticeClick}/>
              ))}
              <Group spacing="center" mt="xs">
                <Button
                  fullWidth={true}
                  variant="light"
                  onClick={loadMore}
                  disabled={!hasMore || isFetching}
                  loading={isFetching && items.length > 0}
                >
                  {hasMore ? 'Load more' : 'No more'}
                </Button>
              </Group>
            </Stack>
          </ScrollArea>
        )}

        <Divider/>

        <Text size="xs" c="dimmed">
          {t("common.notice.drawer.tip", "Tip: Certain notices may trigger SMS based on your notification templates.")}
        </Text>

      </Stack>

      <NoticeFormModal
        title={t("common.actions.new", "New") + " " + t("common.entity.notice", "Notice")}
        opened={noticeFormModalOpened}
        onClose={noticeFormModalHandlers.close}
        onSubmit={onNoticeSubmit}
        submitting={isCreating}/>

      {selectedNotice &&
        <NoticeModal
          title={t("common.entity.notice", "Notice")}
          opened={noticeModalOpened}
          onClose={onNoticeModelClose}
          notice={selectedNotice}/>
      }

    </Drawer>
  )
}

const SCOPE_CONFIG = {
  region: {icon: IconWorld, color: "blue", label: "Region"},
  company: {icon: IconBuilding, color: "orange", label: "Company"},
  audience: {icon: IconUsers, color: "grape", label: "Audience"},
};

type NoticeItemProps = Omit<CardProps, 'children'> & {
  notice: NoticeEntity
  onClick?: (notice: NoticeEntity) => void
}

const NoticeItem = (props: NoticeItemProps) => {
  const {notice, onClick, ...rest} = props

  const {t} = useTranslation(["common"]);

  const [showScopes, setShowScopes] = useState(false);

  const byline = useMemo(() => {
    let value = `${getFormattedDatabaseCode(notice.category, t)} • ${getFormattedDateTime(notice.createDttm)}`
    if (notice.createdByName) {
      value += ` • ${notice.createdByName}`
    }
    return value
  }, [notice.category, notice.createDttm, notice.createdByName, t])

  const expiresLine = useMemo(() => {
    if (!notice.expiresDttm) {
      return t<string>("common.modal.notice.noexpiration", "No expiration");
    }

    if (notice.isExpired()) {
      return `${t<string>("common.modal.notice.expired", "Expired")} • ${getFormattedDateTime(notice.expiresDttm)}`;
    }

    return `${t<string>("common.modal.notice.expires", "Expires")} • ${getFormattedDateTime(notice.expiresDttm)}`;
  }, [notice, t]);

  const renderScopes = (items: string[] | DatabaseCode[] | undefined, type: keyof typeof SCOPE_CONFIG) => {
    if (!items || items.length === 0)
      return null;

    const {icon: Icon, color} = SCOPE_CONFIG[type];

    return items.map((item, index) => (
      <Badge
        key={`${type}-${index}`}
        size="xs"
        variant="light"
        color={color}
        leftSection={<Icon size={12}/>}
      >
        {isDatabaseCode(item) ? getFormattedDatabaseCode(item, t) : item}
      </Badge>
    ));
  };

  return (
    <Card {...rest}>
      <Flex align="flex-start" justify="space-between" gap="sm">
        <Stack spacing={2} style={{textAlign: "left", flex: 1}}>
          <Group spacing="xs" noWrap>
            <Text fw={600} size="sm" lineClamp={1} style={{flex: 1}}>
              {notice.title}
            </Text>
            <Tooltip label={showScopes ? "Hide Scopes" : "Show Scopes"}>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setShowScopes((prev) => !prev)}
              >
                {showScopes ? <IconEyeOff size={16}/> : <IconEye size={16}/>}
              </ActionIcon>
            </Tooltip>
            <NoticeSeverityBadge
              value={notice.severity}
              variant={notice.severity === noticeSeverity.info ? "outline" : "filled"}/>
          </Group>

          {/* Conditional Scope Tags Section */}
          {showScopes && (
            <>
              <Group spacing={6} my="md" noWrap={false}>
                {renderScopes(notice.scopedRegionNames, "region")}
                {renderScopes(notice.scopedCompanyNames, "company")}
                {renderScopes(notice.scopedAudienceCapabilities, "audience")}
              </Group>
              <Divider variant="dashed"/>
            </>
          )}

          <Text size="xs" lineClamp={3} my="md">
            {notice.bodyText}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {byline}
          </Text>
        </Stack>
      </Flex>

      <Flex align="flex-end" justify="space-between" gap="sm">
        <Badge size="xs" color={notice.isExpired() ? "red" : "gray"} variant={notice.isExpired() ? "filled" : "light"}>
          {expiresLine}
        </Badge>
        <Group position="right" mt="md">
          <Button size="xs" c="dimmed" variant="subtle" onClick={() => onClick?.(notice)}>
            {t("common.action.view", "View")}
          </Button>
        </Group>
      </Flex>

    </Card>
  )
}
